/**
 * ArtistStories.js
 *
 * Two exports:
 *   StoriesRail    , horizontal scroll of artist story bubbles (for HomePage)
 *   ArtistStoryView, full-screen story viewer (for ArtistProfilePage)
 *   StoryUpload    , upload new story (shown to artist on their own profile)
 *
 * Stories expire after 24 hours. Media can be audio, image, or short video.
 * View counts increment on open.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { visibleNow } from '../utils/stories';
import StoryTextEditor from './StoryTextEditor';
import { useAuth } from '../contexts/AuthContext';
import { useHaptics } from '../hooks/useHaptics';
import {
  X, Plus, Upload, Loader, Play, Pause, Music, Image, Video,
  Eye, Clock, Download, Heart, Sparkles, Trash2, Type,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeLeft(expiresAt) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return null;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 1) return `${h}h`;
  return `${m}m`;
}

// ── Story Upload ──────────────────────────────────────────────────────────────
export function StoryUpload({ artistId, onUploaded, inline = false }) {
  const { tap } = useHaptics();
  // `inline` was already being PASSED by both call sites, CreateMenuModal
  // and the profile's own + menu, and this component never declared it. So
  // it rendered its trigger button and its own full-screen overlay inside a
  // sheet that was already a modal: you opened "Add Story" and were shown a
  // second "Add Story" button, and tapping that threw a bottom sheet over the
  // card you were already looking at. In inline mode the form is now the
  // content, with no trigger and no overlay.
  const [open, setOpen]         = useState(inline);
  const [file, setFile]         = useState(null);
  const [caption, setCaption]   = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState('');
  const [preview, setPreview]   = useState(null);
  const [taggedTrack, setTaggedTrack] = useState(null); // { id, title, file_url }
  const [showTrackPicker, setShowTrackPicker] = useState(false);
  const [editingText, setEditingText] = useState(false);
  const [myTracks, setMyTracks] = useState([]);
  const fileRef                 = useRef(null);

  // The artist's own stories that have not expired yet.
  //
  // There was no way to see what you had posted and no way to take it down , 
  // the only remedy for a mistake was to wait out the full 24 hours. Now the
  // sheet you post from is also the sheet you manage from.
  const [mine, setMine]             = useState([]);
  const [mineLoading, setMineLoading] = useState(false);
  const [deletingId, setDeletingId]   = useState(null);

  const ACCEPT = 'image/*,audio/*,video/mp4,video/webm';
  const MAX_MB = 50;

  const loadMine = useCallback(async () => {
    if (!artistId) return;
    setMineLoading(true);
    const { data, error } = await visibleNow(
      supabase
        .from('artist_stories')
        .select('id, media_url, media_type, caption, view_count, like_count, reaction_count, expires_at, created_at')
        .eq('artist_id', artistId))
      .order('created_at', { ascending: false });
    if (error) console.error('[story] own stories load failed:', error.code, error.message);
    setMine(data || []);
    setMineLoading(false);
  }, [artistId]);

  useEffect(() => { if (open) loadMine(); }, [open, loadMine]);

  const deleteStory = async (story) => {
    setDeletingId(story.id);
    // The row first. If the row goes and the file lingers, the story is gone
    // from the app and the orphan costs a few KB. Deleting the file first and
    // then failing to delete the row would leave a story that renders as a
    // broken image, the worse of the two failures, so it is the one that
    // cannot happen.
    const { error } = await supabase.from('artist_stories').delete().eq('id', story.id);
    if (error) {
      console.error('[story] delete refused:', error.code, error.message);
      setError(`Could not delete: ${error.message}`);
      setDeletingId(null);
      return;
    }
    // Best-effort file cleanup.
    //
    // This looked right and removed nothing, for a reason worth spelling out:
    // the BUCKET is called stories and the key inside it ALSO starts with
    // stories/, because the upload above writes
    // `stories/${artistId}/${Date.now()}.${ext}`. So a public URL reads
    //
    //   .../object/public/stories/stories/<artistId>/<ts>.<ext>
    //                    ^bucket  ^key
    //
    // and indexOf('/stories/') finds the BUCKET segment, not the key. The old
    // line handed remove() `stories/stories/<artistId>/<ts>.<ext>`, one level
    // too deep, which matches no object. remove() reports that in its return
    // value rather than throwing, and the return value was discarded, so every
    // manually deleted story left its media file sitting in a public bucket.
    // The row vanished from the app and the file stayed reachable by URL to
    // anyone who had it.
    //
    // Split on the bucket segment and keep everything after it, which is the
    // key by definition however many path parts it has.
    try {
      const marker = '/object/public/stories/';
      const i = story.media_url.indexOf(marker);
      const key = i === -1 ? null : story.media_url.slice(i + marker.length).split('?')[0];
      if (key) {
        const { error: rmErr } = await supabase.storage.from('stories').remove([key]);
        // Logged, not shown. The story is already gone as far as the artist is
        // concerned, but a silent failure here is what hid this for months.
        if (rmErr) console.error('[story] media file not removed:', key, rmErr.message);
      }
    } catch (err) {
      console.error('[story] media cleanup threw:', err?.message);
    }
    setMine(prev => prev.filter(x => x.id !== story.id));
    setDeletingId(null);
    onUploaded?.();
  };

  const openTrackPicker = async () => {
    if (!myTracks.length) {
      const { data } = await supabase.from('tracks')
        .select('id, title, cover_artwork_url, file_url')
        .eq('artist_id', artistId).eq('is_published', true)
        .order('created_at', { ascending: false }).limit(30);
      setMyTracks(data || []);
    }
    setShowTrackPicker(true);
  };

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { setError(`Max file size is ${MAX_MB}MB`); return; }
    setFile(f);
    setError('');
    // Always create object URL for preview, works for all types
    setPreview(URL.createObjectURL(f));
  };

  const convertToMp4 = async (videoFile) => {
    // Read file as base64
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload  = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(videoFile);
    });

    // The function now requires a caller token, so it cannot be used by anyone
    // who is not signed in.
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/.netlify/functions/convert-to-mp4-background', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
      body: JSON.stringify({ video: base64, mimeType: videoFile.type }),
    });

    if (!res.ok) throw new Error('Video conversion failed, try uploading an MP4 directly');
    const { mp4 } = await res.json();

    // Convert base64 back to File
    const bytes = Uint8Array.from(atob(mp4), c => c.charCodeAt(0));
    const blob  = new Blob([bytes], { type: 'video/mp4' });
    return new File([blob], videoFile.name.replace(/\.[^.]+$/, '.mp4'), { type: 'video/mp4' });
  };

  const mediaType = file
    ? file.type.startsWith('audio') ? 'audio'
    : file.type.startsWith('video') ? 'video'
    : 'image'
    : null;

  const handleUpload = async () => {
    if (!file || !artistId) return;
    setUploading(true);
    setError('');
    try {
      let uploadFile = file;

      // Convert WebM/video to MP4 for universal playback (Safari, iOS etc)
      if (mediaType === 'video' && (file.type === 'video/webm' || file.name.endsWith('.webm'))) {
        setError('Converting video…');
        uploadFile = await convertToMp4(file);
        setError('');
      }

      const ext  = uploadFile.name.split('.').pop().toLowerCase();
      const storagePath = `stories/${artistId}/${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('stories')
        .upload(storagePath, uploadFile, { upsert: false, contentType: uploadFile.type });
      if (upErr) throw upErr;

      const { data: { publicUrl } } = supabase.storage.from('stories').getPublicUrl(storagePath);

      // The error was discarded here.
      //
      // The file uploaded to storage, this line ran, whatever the database
      // said was thrown away, the modal closed and everything looked like it
      // had worked. If the row was refused there was no story and no message
      // saying so, which is exactly the "I uploaded a story but it's not
      // showing" symptom, and it is unfalsifiable from the outside because
      // success and failure produce identical screens.
      const { error: insErr } = await supabase.from('artist_stories').insert({
        artist_id:       artistId,
        media_url:       publicUrl,
        media_type:      mediaType,
        caption:         caption.trim() || null,
        tagged_track_id: taggedTrack?.id || null,
        expires_at:      new Date(Date.now() + 24 * 3600000).toISOString(),
      });
      if (insErr) {
        console.error('[story] insert refused:', insErr.code, insErr.message, insErr.details || '', insErr.hint || '');
        throw new Error(
          insErr.code === '42501' || /row-level security/i.test(insErr.message)
            ? 'The file uploaded but the story could not be saved, your account is not allowed to post stories for this artist.'
            : `Story could not be saved: ${insErr.message}`
        );
      }

      setFile(null);
      setCaption('');
      setPreview(null);
      setTaggedTrack(null);
      loadMine();
      if (!inline) setOpen(false);
      onUploaded?.();
    } catch (err) { setError(err.message); }
    setUploading(false);
  };

  // ── The form body, shared by both modes ────────────────────────────────
  const body = (
    <>
      <p className="text-xs text-white/35 mb-4 leading-relaxed">
        Stories disappear after 24 hours. Share audio clips, images, or short
        videos with your followers.
      </p>

      {/* Your live stories, see them, and take one down.
          This is the half that did not exist. An artist could post and then
          had no view of what was up and no way to remove it short of waiting
          out the full day. */}
      {(mineLoading || mine.length > 0) && (
        <div className="mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/30 mb-2">
            Live now {mine.length > 0 && `(${mine.length})`}
          </p>
          {mineLoading ? (
            <div className="py-4 flex justify-center"><Loader className="w-4 h-4 animate-spin text-white/25" /></div>
          ) : (
            <div className="space-y-2">
              {mine.map(st => (
                <div key={st.id} className="flex items-center gap-3 p-2 rounded-xl bg-white/[0.04] border border-white/[0.06]">
                  <div className="w-11 h-11 rounded-lg overflow-hidden bg-black flex-shrink-0 flex items-center justify-center">
                    {st.media_type === 'image'
                      ? <img src={st.media_url} alt="" className="w-full h-full object-cover" />
                      : st.media_type === 'video'
                        ? <Video className="w-5 h-5 text-white/35" />
                        : <Music className="w-5 h-5 text-white/35" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-white/80 truncate">
                      {st.caption || `${st.media_type[0].toUpperCase()}${st.media_type.slice(1)} story`}
                    </p>
                    <div className="flex items-center gap-2.5 mt-0.5 text-[10px] text-white/30">
                      <span className="inline-flex items-center gap-1"><Eye className="w-2.5 h-2.5" />{st.view_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><Heart className="w-2.5 h-2.5" />{st.reaction_count ?? st.like_count ?? 0}</span>
                      <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{timeLeft(st.expires_at) || 'expiring'}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => deleteStory(st)}
                    disabled={deletingId === st.id}
                    title="Delete this story"
                    className="w-8 h-8 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-red-500/20 transition active:scale-90 flex-shrink-0 disabled:opacity-40"
                  >
                    {deletingId === st.id
                      ? <Loader className="w-3.5 h-3.5 animate-spin text-white/40" />
                      : <Trash2 className="w-3.5 h-3.5 text-white/40" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* File picker */}
      {!file ? (
        <button onClick={() => fileRef.current?.click()}
          className="w-full py-8 rounded-2xl border-2 border-dashed border-white/15 flex flex-col items-center space-y-2 text-white/30 hover:border-white/25 hover:text-white/50 transition mb-3">
          <Upload className="w-8 h-8" />
          <span className="text-sm">Tap to choose audio, image, or video</span>
          <span className="text-[10px] text-white/20 mt-1">Videos convert to MP4 automatically</span>
          <span className="text-xs">Max {MAX_MB}MB</span>
        </button>
      ) : (
        <div className="rounded-2xl overflow-hidden bg-black mb-3 relative">
          {mediaType === 'image' && <img src={preview} alt="" className="w-full max-h-48 object-contain" />}
          {mediaType === 'audio' && (
            <div className="flex items-center space-x-3 p-4">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Music className="w-5 h-5 text-purple-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{file.name}</p>
                <audio controls src={preview} className="mt-1 w-full" style={{ height: 32 }} />
              </div>
            </div>
          )}
          {mediaType === 'video' && <video src={preview} controls className="w-full max-h-48" />}
          <button onClick={() => { setFile(null); setPreview(null); }}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
            <X className="w-3.5 h-3.5 text-white" />
          </button>
          {/* Images only. Text on a video means rendering every frame, which
              is a different feature with a different cost. */}
          {mediaType === 'image' && (
            <button onClick={() => setEditingText(true)}
              className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 px-3 h-8 rounded-full
                         bg-black/65 border border-white/20 text-[11px] font-semibold text-white">
              <Type className="w-3.5 h-3.5" />
              Add text
            </button>
          )}
        </div>
      )}

      <input ref={fileRef} type="file" accept={ACCEPT} onChange={handleFile} className="hidden" />

      <input value={caption} onChange={e => setCaption(e.target.value)} maxLength={150}
        placeholder="Add a caption (optional)"
        className="w-full bg-white/[0.06] rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/20 outline-none mb-3" />

      {error && <p className="text-xs text-red-400 mb-3">{error}</p>}

      <button onClick={handleUpload} disabled={!file || uploading}
        className="w-full py-3 bg-purple-600 rounded-xl text-sm font-semibold text-white disabled:opacity-40 transition flex items-center justify-center space-x-2">
        {uploading ? <Loader className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        <span>{uploading ? (error === 'Converting video…' ? 'Converting...' : 'Uploading...') : 'Share Story'}</span>
      </button>
    </>
  );

  const textEditor = editingText && file ? (
    <StoryTextEditor
      file={file}
      onCancel={() => setEditingText(false)}
      onDone={(edited) => {
        // Everything downstream reads `file`, so swapping it is the whole
        // integration: the preview, the upload, the extension and the content
        // type all follow. Nothing about storage or the stories table changes,
        // which is the point of flattening the text into the image.
        setFile(edited);
        setPreview(URL.createObjectURL(edited));
        setEditingText(false);
      }}
    />
  ) : null;

  // Inline: the caller already owns a modal shell. Render the content only , 
  // no trigger button, no second overlay.
  if (inline) return <div>{body}{textEditor}</div>;

  return (
    <>
      <button
        onClick={() => { tap(); setOpen(true); }}
        className="flex-shrink-0 flex flex-col items-center space-y-1.5"
      >
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center bg-white/[0.04] hover:bg-white/[0.07] transition">
          <Plus className="w-6 h-6 text-white/40" />
        </div>
        <span className="text-[10px] text-white/30">Add Story</span>
      </button>

      {/* A centre-floating card, matching CreateMenuModal and MerchConnectSheet.
          This was the only sheet in the app that slid up from the bottom edge
          and squared off its top corners, so it read as a different app's
          component every time it appeared. Same shell as the others now:
          items-center, max-w-sm, rounded-3xl, #0f0f0f, a header row with a
          close button, and its own scroll so a long list of live stories
          cannot push the Share button off a short screen. */}
      {open && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4 py-6"
          onClick={() => setOpen(false)}>
          <div className="w-full max-w-sm rounded-3xl overflow-hidden flex flex-col"
            style={{ maxHeight: 'calc(100vh - 48px)', backgroundColor: '#0f0f0f', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 32px 64px rgba(0,0,0,0.6)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
              <p className="text-sm font-bold text-white">Add a Story</p>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/[0.08] hover:bg-white/[0.15] transition">
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1">{body}</div>
          </div>
        </div>
      )}
      {textEditor}
    </>
  );
}

// ── The platform's own account ────────────────────────────────────────────────
//
// Migration 191 creates this artist row with artist_name 'Feelz Machine' and
// slug 'feelz-machine', and sets no profile image, because the artists table
// has no column it could have put one in that the migration was willing to
// guess at. So every bubble, every story header and every mention of the
// platform account fell through to the first letter of the name and rendered
// as a grey circle with an F in it.
//
// Matched on the slug rather than REACT_APP_PLATFORM_ARTIST_ID. That env var
// is read a few lines down and, by the admission of the comment next to it,
// has never been set, so anything depending on it does nothing. The slug is
// what migration 191 creates, what platform_story_artist_id() looks up, and
// what every other half of this feature already keys on.
const PLATFORM_SLUG = 'feelz-machine';
const PLATFORM_LOGO = '/logo.png';

export function isPlatformArtist(artist, envId) {
  if (!artist) return false;
  if (envId && artist.id === envId) return true;
  return artist.slug === PLATFORM_SLUG;
}

// What to draw in the circle. Null means there is genuinely nothing, and the
// caller falls back to the initial, which is still the right answer for an
// ordinary artist who has not uploaded a picture.
export function storyAvatarSrc(artist) {
  if (artist?.profile_image_url) return artist.profile_image_url;
  if (artist?.slug === PLATFORM_SLUG) return PLATFORM_LOGO;
  return null;
}

// The logo is a rounded square on black, not a photograph. object-cover would
// crop its corners against a circular mask and cut the green outline, so it is
// inset and contained instead. An artist's own photo still covers, because
// cropping a photo to a circle is correct.
export function storyAvatarClass(artist) {
  return artist?.profile_image_url
    ? 'w-full h-full object-cover'
    : 'w-full h-full object-contain p-1.5 bg-black';
}

// ── Story Bubble ──────────────────────────────────────────────────────────────
//
// Two modes.
//
// GROUPED, one bubble per artist, every story behind it. This is the normal
// rail and it is what every app does, because with twenty artists posting you
// want twenty faces, not two hundred circles.
//
// EXPANDED, one bubble per story, used when almost nobody has posted. The
// grouped rail is correct and still looks broken on a quiet day: five stories
// from the platform account collapse into a single circle sitting alone in a
// full width panel, which reads as "there is nothing here" when there are
// actually five things here. In expanded mode each story gets its own bubble,
// showing its own image where it has one, so the rail fills with what is
// actually there.
function StoryBubble({ artist, stories, story, label, viewed, onClick }) {
  const hasUnviewed = story ? !viewed.has(story.id) : stories.some(s => !viewed.has(s.id));
  const src   = story?.media_type === 'image' ? story.media_url : storyAvatarSrc(artist);
  const cls   = story?.media_type === 'image' ? 'w-full h-full object-cover' : storyAvatarClass(artist);
  const badge = story && story.media_type !== 'image' ? story.media_type : null;
  return (
    <button onClick={onClick}
      className="flex-shrink-0 flex flex-col items-center space-y-1.5 w-[68px]">
      {/* The ring, made to read as a ring.
          It was a 2px (p-0.5) two-stop gradient, which at 64px across is a
          hairline, on a dark page next to full-colour artwork it reads as an
          edge on the avatar rather than as the "there is something new here"
          signal every other app has trained people to look for. Three
          changes: 3px so it has actual width, a three-stop gradient so it
          does not flatten into one purple, and a soft coloured glow so it
          separates from the black behind it. Viewed stories stay deliberately
          flat and grey, the contrast between the two states is the whole
          point, and brightening both would have destroyed it. */}
      <div
        className={`w-16 h-16 rounded-full ${hasUnviewed
          ? 'p-[3px] bg-gradient-to-tr from-fuchsia-500 via-pink-500 to-amber-400'
          : 'p-[2px] bg-white/15'}`}
        style={hasUnviewed ? { boxShadow: '0 0 12px rgba(236,72,153,0.45)' } : undefined}
      >
        <div className="relative w-full h-full rounded-full overflow-hidden bg-black border-2 border-black">
          {src
            ? <img src={src} alt={artist.artist_name} className={cls} />
            : <div className="w-full h-full bg-white/10 flex items-center justify-center text-sm font-bold text-white/40">
                {artist.artist_name?.[0]}
              </div>}
          {/* An expanded bubble for audio or video has no still to show, so it
              says which it is rather than looking like a duplicate of the one
              beside it. */}
          {badge && (
            <span className="absolute bottom-0 right-0 w-5 h-5 rounded-full bg-black/80 border border-white/15 flex items-center justify-center">
              {badge === 'video'
                ? <Video className="w-2.5 h-2.5 text-white/70" />
                : <Music className="w-2.5 h-2.5 text-white/70" />}
            </span>
          )}
        </div>
      </div>
      <span className={`text-[10px] truncate max-w-[68px] ${hasUnviewed ? 'text-white/80 font-semibold' : 'text-white/40'}`}>
        {label || artist.artist_name}
      </span>
    </button>
  );
}

// The five, and they are the same five the database allows in migration 213's
// check constraint. If this list and that constraint ever disagree, the one
// that is wrong is this one, because the database will simply refuse the row.
const REACTIONS = ['❤️', '🔥', '😂', '😮', '👏'];

// ── Full-screen Story Viewer ──────────────────────────────────────────────────
export function ArtistStoryView({ stories, artist, initialIndex = 0, onClose }) {
  const { user } = useAuth();
  const { tap }  = useHaptics();
  const navigate = useNavigate();
  const [idx, setIdx]               = useState(initialIndex);
  const [playing, setPlaying]       = useState(false);
  const [progress, setProgress]     = useState(0);
  // myReaction: { [storyId]: emoji }. reactionCounts: { [storyId]: number }.
  const [myReaction, setMyReaction]         = useState({});
  const [reactionCounts, setReactionCounts] = useState({});
  const taggedAudioRef              = useRef(null);
  const audioRef                    = useRef(null);
  const videoRef                    = useRef(null);
  const progressRef                 = useRef(null);
  const DURATION_IMAGE_MS           = 5000;

  // ── PRESS AND HOLD TO PAUSE ───────────────────────────────────────────────
  //
  // Tap is already spoken for. The left and right thirds of the media are prev
  // and next, and that is the first thing a thumb reaches for, so pause cannot
  // be a tap without taking navigation away. It is the other gesture every
  // stories UI uses instead: press and hold to pause, release to resume.
  //
  // HOLD_MS is the line between the two. Short enough that it feels immediate
  // when you mean it, long enough that an ordinary tap never trips it.
  //
  // One wrinkle, and it is the whole reason suppressNavRef exists: a click
  // event fires after pointerup, on the element the finger was over. Release a
  // hold while over the right third and the browser will also report a tap
  // there, which would skip the story the reader was in the middle of. So the
  // release of a hold sets a flag that the tap zones consume, and only the tap
  // zones, because the image timer and onEnded call goNext too and neither of
  // those should ever be swallowed.
  const HOLD_MS                     = 250;
  const [held, setHeld]             = useState(false);
  const heldRef                     = useRef(false);
  const holdTimerRef                = useRef(null);
  const suppressNavRef              = useRef(false);

  const setHoldState = (on) => { heldRef.current = on; setHeld(on); };

  const pauseMedia = () => {
    try { videoRef.current?.pause(); } catch {}
    try { audioRef.current?.pause(); } catch {}
  };

  // play() hands back a promise that rejects when the element has gone away or
  // the browser refuses, and an unhandled rejection in here would print on
  // every single release.
  const resumeMedia = () => {
    try { videoRef.current?.play()?.catch(() => {}); } catch {}
    try { audioRef.current?.play()?.catch(() => {}); } catch {}
  };

  const onHoldStart = (e) => {
    // The audio and video elements carry their own native controls, and the
    // centre of the screen is where those controls are. A press that lands on
    // one of them belongs to the browser, not to this gesture.
    const tag = e?.target?.tagName;
    if (tag === 'VIDEO' || tag === 'AUDIO') return;
    suppressNavRef.current = false;
    clearTimeout(holdTimerRef.current);
    holdTimerRef.current = setTimeout(() => {
      setHoldState(true);
      pauseMedia();
    }, HOLD_MS);
  };

  const onHoldEnd = () => {
    clearTimeout(holdTimerRef.current);
    if (!heldRef.current) return;
    suppressNavRef.current = true;
    setHoldState(false);
    resumeMedia();
  };

  const onZoneTap = (fn) => (e) => {
    e.stopPropagation();
    if (suppressNavRef.current) { suppressNavRef.current = false; return; }
    fn();
  };

  // ── REACTIONS ─────────────────────────────────────────────────────────────
  //
  // This was a single heart, and the notification telling the artist about it
  // was written by the browser. Two things wrong with that, and the second is
  // the one that mattered: if the tab closed, if the insert was refused, if
  // somebody tapped and immediately swiped on, the reaction existed in the
  // database and the artist never found out.
  //
  // Migration 213 moved the notification into a trigger, so it fires because
  // the row exists rather than because a tab stayed open. Which also means
  // none of that sending code belongs here any more.
  //
  // ONE REACTION EACH, and the database enforces it. Tapping a different emoji
  // replaces yours. Tapping the one you already chose takes it back. Because
  // the write is an upsert, changing your mind resolves to an UPDATE, the
  // AFTER INSERT trigger does not fire, and the artist is not told twice about
  // the same person.
  useEffect(() => {
    const counts = {};
    stories.forEach(s => { counts[s.id] = s.reaction_count ?? s.like_count ?? 0; });
    setReactionCounts(counts);

    if (!user) { setMyReaction({}); return; }
    const ids = stories.map(s => s.id);
    if (!ids.length) return;
    supabase.from('story_reactions').select('story_id, emoji')
      .eq('user_id', user.id).in('story_id', ids)
      .then(({ data, error }) => {
        if (error) { console.warn('[story] reactions unavailable:', error.code, error.message); return; }
        const mine = {};
        (data || []).forEach(r => { mine[r.story_id] = r.emoji; });
        setMyReaction(mine);
      });
  }, [stories, user]);

  const react = async (e, emoji) => {
    e.stopPropagation();
    if (!user || !story) return;
    tap();
    const storyId = story.id;
    const current = myReaction[storyId];
    const removing = current === emoji;

    // Optimistic, because a reaction that waits for a round trip before it
    // moves feels broken on the one screen where everything is a quick tap.
    setMyReaction(prev => {
      const next = { ...prev };
      if (removing) delete next[storyId]; else next[storyId] = emoji;
      return next;
    });
    setReactionCounts(prev => ({
      ...prev,
      [storyId]: Math.max(0, (prev[storyId] || 0) + (removing ? -1 : current ? 0 : 1)),
    }));

    const { error } = removing
      ? await supabase.from('story_reactions').delete()
          .eq('story_id', storyId).eq('user_id', user.id)
      : await supabase.from('story_reactions')
          .upsert({ story_id: storyId, user_id: user.id, emoji }, { onConflict: 'story_id,user_id' });

    if (error) {
      console.error('[story] reaction failed:', error.code, error.message);
      // Put it back rather than leaving somebody believing they reacted.
      setMyReaction(prev => {
        const next = { ...prev };
        if (removing) next[storyId] = current; else if (current) next[storyId] = current; else delete next[storyId];
        return next;
      });
      setReactionCounts(prev => ({
        ...prev,
        [storyId]: Math.max(0, (prev[storyId] || 0) + (removing ? 1 : current ? 0 : -1)),
      }));
    }
  };

  const story = stories[idx];

  const markViewed = useCallback(async (storyId) => {
    if (!user) return;
    try {
      await supabase.from('story_views').upsert({ story_id: storyId, user_id: user.id }, { onConflict: 'story_id,user_id', ignoreDuplicates: true });
      try {
        await supabase.rpc('increment_story_views', { story_id_input: storyId });
      } catch {
        try { await supabase.from('artist_stories').update({ view_count: (story.view_count || 0) + 1 }).eq('id', storyId); } catch {}
      }
    } catch {}
  }, [user, story?.view_count]);

  useEffect(() => {
    if (!story) return;
    markViewed(story.id);
    setProgress(0);
    setPlaying(false);
    // A new story starts unheld, whatever the finger was doing on the last one.
    clearTimeout(holdTimerRef.current);
    setHoldState(false);
    suppressNavRef.current = false;

    if (story.media_type === 'image') {
      clearInterval(progressRef.current);
      let elapsed = 0;
      progressRef.current = setInterval(() => {
        // Held means the clock stops. The interval keeps ticking rather than
        // being torn down and rebuilt, so elapsed is never lost and the bar
        // picks up exactly where it stopped.
        if (heldRef.current) return;
        elapsed += 100;
        setProgress(Math.min(100, (elapsed / DURATION_IMAGE_MS) * 100));
        if (elapsed >= DURATION_IMAGE_MS) {
          clearInterval(progressRef.current);
          goNext();
        }
      }, 100);
    }
    return () => { clearInterval(progressRef.current); clearTimeout(holdTimerRef.current); };
  }, [idx]);

  const goNext = () => {
    tap();
    if (idx < stories.length - 1) setIdx(p => p + 1);
    else onClose();
  };

  const goPrev = () => {
    tap();
    if (idx > 0) setIdx(p => p - 1);
  };

  if (!story) return null;

  return (
    <div className="fixed inset-0 z-[700] bg-black flex flex-col">
      {/* Progress bars */}
      <div className="flex space-x-1 px-3 pt-safe pt-4 flex-shrink-0">
        {stories.map((s, i) => (
          <div key={s.id} className="flex-1 h-0.5 rounded-full bg-white/20 overflow-hidden">
            <div className="h-full bg-white rounded-full transition-none"
              style={{ width: i < idx ? '100%' : i === idx ? `${progress}%` : '0%' }} />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10">
            {storyAvatarSrc(artist)
              ? <img src={storyAvatarSrc(artist)} alt="" className={storyAvatarClass(artist)} />
              : <div className="w-full h-full flex items-center justify-center text-xs font-bold text-white/40">{artist.artist_name?.[0]}</div>}
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{artist.artist_name}</p>
            <div className="flex items-center space-x-1">
              <Clock className="w-2.5 h-2.5 text-white/30" />
              <span className="text-[10px] text-white/30">{timeLeft(story.expires_at)} left</span>
              {story.view_count > 0 && (
                <>
                  <Eye className="w-2.5 h-2.5 text-white/30 ml-1" />
                  <span className="text-[10px] text-white/30">{story.view_count}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {(story.media_type === 'image' || story.media_type === 'video') && (
            <button onClick={() => {
              const a = document.createElement('a');
              a.href = story.media_url;
              a.download = `feelzmachine-story.${story.media_type === 'video' ? 'mp4' : 'png'}`;
              a.target = '_blank';
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
            }} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition">
              <Download className="w-4 h-4 text-white/70" />
            </button>
          )}
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition">
            <X className="w-4 h-4 text-white/70" />
          </button>
        </div>
      </div>

      {/* Media */}
      <div
        className="flex-1 flex items-center justify-center relative overflow-hidden"
        onPointerDown={onHoldStart}
        onPointerUp={onHoldEnd}
        onPointerCancel={onHoldEnd}
        onPointerLeave={onHoldEnd}
        // Without this, a long press on iOS raises the save-image callout over
        // the story instead of pausing it. The header already has a download
        // button, so nothing is lost by taking the callout away.
        style={{ touchAction: 'manipulation', WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
      >
        {story.media_type === 'image' && (
          <img src={story.media_url} alt={story.caption || ''} className="max-w-full max-h-full object-contain" />
        )}
        {story.media_type === 'audio' && (
          <div className="flex flex-col items-center space-y-6 px-8">
            <div className="w-32 h-32 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Music className="w-16 h-16 text-purple-400" />
            </div>
            <audio ref={audioRef} src={story.media_url} controls
              className="w-full" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
              onEnded={goNext} autoPlay />
          </div>
        )}
        {story.media_type === 'video' && (
          <video
            muted={!!story.tracks?.file_url} ref={videoRef} src={story.media_url} controls autoPlay
            className="max-w-full max-h-full" onEnded={goNext} />
        )}

        {/* Tap zones for navigation. onZoneTap swallows the click that follows
            the release of a press and hold, so pausing to read never costs you
            the story you were reading. */}
        <button className="absolute left-0 top-0 bottom-0 w-1/3" onClick={onZoneTap(goPrev)} />
        <button className="absolute right-0 top-0 bottom-0 w-1/3" onClick={onZoneTap(goNext)} />

        {/* Paused marker. Held is a deliberate gesture and it needs to be
            visibly acknowledged, otherwise the first thing a reader does when
            the bar stops is let go to check whether it worked. */}
        {held && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full pointer-events-none"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <span className="text-[10px] font-semibold tracking-wider uppercase text-white/70">Paused</span>
          </div>
        )}
      </div>

      {/* Bottom bar, track pill + like */}
      <div className="absolute bottom-4 left-4 right-4 z-20 flex items-end justify-between">
        {story.tracks ? (
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/track/${story.tracks.slug || story.tracks.id}`); }}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-full active:scale-95 transition"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.2)', maxWidth: 'calc(100% - 64px)' }}>
            {story.tracks.cover_artwork_url
              ? <img src={story.tracks.cover_artwork_url} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
              : <span className="text-sm flex-shrink-0">🎵</span>}
            <div className="flex flex-col min-w-0">
              <p className="text-[11px] font-bold text-white truncate">{story.tracks.title}</p>
              <p className="text-[9px] text-purple-300/80 uppercase tracking-wider">Listen now →</p>
            </div>
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); navigate(`/artist/${artist.slug}`); }}
            className="inline-flex items-center space-x-2 px-3 py-2 rounded-full active:scale-95 transition"
            style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <p className="text-[11px] font-semibold text-white/70">{artist.artist_name}</p>
            <p className="text-[9px] text-purple-300/60">View profile →</p>
          </button>
        )}

        {(reactionCounts[story.id] || 0) > 0 && (
          <span className="ml-3 flex-shrink-0 text-[11px] font-semibold text-white/45 self-center">
            {reactionCounts[story.id]}
          </span>
        )}
      </div>

      {/* THE REACTION ROW.
          Its own row rather than a button beside the track pill. Five targets
          of 44px plus the pill does not fit across a phone, and the first
          casualty of squeezing them in is the tap target, on the one screen
          where every interaction is a quick tap with a thumb.

          IN FLOW, NOT ABSOLUTE, AND THAT IS THE FIX.
          It used to be pinned at bottom-20, which is a guess about how tall
          the things below it are. The caption sits in normal flow underneath,
          so the moment a caption ran to a second line the emoji landed on top
          of the words. Nothing about a fixed offset can know that.
          Now it is an ordinary block directly above the caption, so the two
          can never occupy the same space whatever either of them contains.

          Signed out there is nothing to show: a row of emoji that silently do
          nothing is worse than no row. */}
      {user && (
        <div className="relative z-20 flex-shrink-0 flex items-center justify-center gap-2 px-4 pt-3 pb-1">
          {REACTIONS.map(emoji => {
            const mine = myReaction[story.id] === emoji;
            return (
              <button
                key={emoji}
                onClick={(e) => react(e, emoji)}
                aria-label={mine ? `Remove ${emoji}` : `React ${emoji}`}
                aria-pressed={mine}
                className={`w-11 h-11 rounded-full flex items-center justify-center text-xl leading-none
                            transition active:scale-90 ${mine ? 'scale-110' : ''}`}
                style={{
                  background: mine ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.5)',
                  border: mine ? '1px solid rgba(255,255,255,0.55)' : '1px solid rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(12px)',
                }}
              >
                {emoji}
              </button>
            );
          })}
        </div>
      )}

      {/* Caption */}
      {story.caption && (
        <div className="px-5 pt-2 pb-20 flex-shrink-0">
          <p className="text-sm text-white/80 leading-relaxed text-center">{story.caption}</p>
        </div>
      )}
    </div>
  );
}

// ── Stories Rail (for HomePage) ───────────────────────────────────────────────
export function StoriesRail({ userId }) {
  const navigate                    = useNavigate();
  const [storyGroups, setStoryGroups] = useState([]); // [{ artist, stories }]
  const [viewedIds, setViewedIds]   = useState(new Set());
  const [viewing, setViewing]       = useState(null); // { artist, stories, idx }
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        // Pins the platform's own stories to the front of the rail. This
        // already existed and reads an env var; migration 191 creates the
        // artist it is meant to point at, looked up by the slug
        // 'feelz-machine'. Set REACT_APP_PLATFORM_ARTIST_ID to that id, or
        // leave it unset and the pin simply does nothing, which is what it
        // has been doing.
        const platformArtistId = process.env.REACT_APP_PLATFORM_ARTIST_ID;

        // Get all active (non-expired) stories
        // visibleNow, not a bare expires_at check.
        //
        // This is the fifth story read in the app and the one that was missed
        // when publish_at was added, because it lives in a different component
        // in this same file rather than in one of the pages. It is also the
        // widest: no artist filter, limit 100, every live story on the
        // platform. Left as it was, it is the one surface that would have
        // shown December's planned stories in September, to the two people
        // the database policy exempts.
        const { data: stories } = await visibleNow(
          supabase
            .from('artist_stories')
            .select('*, tracks:tagged_track_id(id, title, slug, file_url, cover_artwork_url), artists(id, artist_name, slug, profile_image_url, user_id)'))
          .order('created_at', { ascending: false })
          .limit(100);

        if (!stories?.length) { setLoading(false); return; }

        // Group by artist
        const groups = {};
        stories.forEach(s => {
          const aid = s.artists?.id;
          if (!aid) return;
          if (!groups[aid]) groups[aid] = { artist: s.artists, stories: [] };
          groups[aid].stories.push(s);
        });

        const groupList = Object.values(groups);

        // Pin the platform's stories first.
        //
        // This matched on REACT_APP_PLATFORM_ARTIST_ID alone, which is not
        // set, so the pin has never once fired. isPlatformArtist falls back to
        // the slug migration 191 creates, which needs no configuration and
        // cannot drift per environment.
        const platformIdx = groupList.findIndex(g => isPlatformArtist(g.artist, platformArtistId));
        if (platformIdx > 0) {
          const [platform] = groupList.splice(platformIdx, 1);
          groupList.unshift(platform);
        }
        setStoryGroups(groupList);

        // Get viewed story IDs for this user
        if (userId) {
          const storyIds = stories.map(s => s.id);
          const { data: views } = await supabase
            .from('story_views').select('story_id')
            .eq('user_id', userId).in('story_id', storyIds);
          setViewedIds(new Set((views || []).map(v => v.story_id)));
        }
      } catch {}
      setLoading(false);
    };
    load();
  }, [userId]);

  if (loading || !storyGroups.length) return null;

  // ── What the rail actually shows ──────────────────────────────────────────
  //
  // Grouped by artist once enough artists are posting, expanded to one bubble
  // per story when they are not. See StoryBubble for why.
  //
  // EXPAND_BELOW is 3 because 2 bubbles in a full width panel still reads as
  // empty and 3 reads as a row. Capped at MAX_EXPANDED so a day when the
  // platform posts twenty times does not produce a rail nobody can scroll to
  // the end of.
  const EXPAND_BELOW = 3;
  const MAX_EXPANDED = 12;

  const expanded = storyGroups.length < EXPAND_BELOW;

  const bubbles = expanded
    ? storyGroups.flatMap(({ artist, stories }) =>
        stories.map((story, idx) => ({
          key:   story.id,
          artist,
          // The viewer still receives the whole group, so opening the third
          // bubble starts on the third story and carries on through the rest
          // rather than closing after one.
          stories,
          idx,
          story,
          label: story.caption?.trim() || artist.artist_name,
        }))
      ).slice(0, MAX_EXPANDED)
    : storyGroups.map(({ artist, stories }) => ({
        key: artist.id, artist, stories, idx: 0, story: null, label: null,
      }));

  return (
    <>
      {/* Given a heading and real vertical space.
          On Home this sat directly under "Welcome back" as a lone avatar with
          no label and no margin, it looked like a stray account chip rather
          than a row of stories, which is exactly how it was being read. Every
          other rail on that page has a labelled header; this one now matches. */}
      <div className="mb-5">
        <div className="flex items-center space-x-2 mb-3 px-6">
          <Sparkles className="w-3.5 h-3.5 text-pink-400/70" />
          <span className="section-label">Stories</span>
          <span className="text-[10px] text-white/25">24h</span>
        </div>
        <div className="flex space-x-4 overflow-x-auto px-6 pb-1 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
        {bubbles.map(({ key, artist, stories, idx, story, label }) => (
          <StoryBubble
            key={key}
            artist={artist}
            stories={stories}
            story={story}
            label={label}
            viewed={viewedIds}
            onClick={() => setViewing({ artist, stories, idx })}
          />
        ))}
        </div>
      </div>

      {viewing && (
        <ArtistStoryView
          stories={viewing.stories}
          artist={viewing.artist}
          initialIndex={viewing.idx}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}